import { useState } from "react";
import { ArrowRight, ChevronDown, Ruler } from "lucide-react";
import { useRequestModal } from "./RequestModal";

export default function DumpsterCard({ size, label, capacity, description, uses, popular, frontload = false, image, imageAlt, imagePos, specs, onCTA }) {
  const { openRequest } = useRequestModal();
  const [specsOpen, setSpecsOpen] = useState(false);
  const handleCTA = () => {
    if (onCTA) onCTA(`${size} Yard`);
    else openRequest(`${size} Yard`);
  };
  return (
    <div
      className={`group relative bg-white rounded-xl border overflow-hidden flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${popular ? "border-sky-500 shadow-lg ring-1 ring-sky-500/20" : "border-slate-200"}`}
      data-testid={`dumpster-card-${size}`}
    >
      {popular && (
        <span className="absolute top-3 right-3 z-10 bg-sky-600 text-white text-[11px] font-heading font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">
          Most Popular
        </span>
      )}
      {image && (
        <div className="overflow-hidden">
          <img
            src={image}
            alt={imageAlt || `${label} YWS dumpster`}
            loading="lazy"
            className={`w-full aspect-[4/3] object-cover ${imagePos || "object-center"} group-hover:scale-[1.04] transition-transform duration-500`}
            data-testid={`dumpster-card-${size}-image`}
          />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="font-heading font-bold text-2xl text-slate-900">{label}</h3>
          <span className="text-xs font-medium text-slate-500">{capacity}</span>
        </div>
        {specs && (
          <p className="text-xs font-semibold text-slate-500 mb-2" data-testid={`dumpster-card-${size}-dimensions`}>
            {specs.dimensions}
          </p>
        )}
        <p className="text-sm text-slate-600 mb-4">{description}</p>
        {uses && (
          <ul className="space-y-1.5 mb-5">
            {uses.map((u) => (
              <li key={u} className="flex items-center gap-2 text-sm text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0" aria-hidden="true" />
                {u}
              </li>
            ))}
          </ul>
        )}
        {specs && (
          <div className="mb-5">
            <button
              type="button"
              onClick={() => setSpecsOpen((o) => !o)}
              aria-expanded={specsOpen}
              className="inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-sky-700 hover:text-sky-800 transition-colors"
              data-testid={`dumpster-card-${size}-specs-toggle`}
            >
              <Ruler className="h-4 w-4" />
              Full Specs
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${specsOpen ? "rotate-180" : ""}`} />
            </button>
            <div
              className={`grid transition-all duration-300 ease-out ${specsOpen ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0"}`}
            >
              <div className="overflow-hidden">
                <dl className="space-y-4 text-sm" data-testid={`dumpster-card-${size}-specs`}>
                  <div className="bg-slate-50 rounded-lg px-4 py-3">
                    <dt className="font-heading font-semibold text-slate-900 text-xs uppercase tracking-wider mb-1.5">Capacity</dt>
                    <dd>
                      <ul className="space-y-1">
                        {specs.capacity.map((c) => (
                          <li key={c} className="flex items-center gap-2 text-slate-700">
                            <span className="h-1 w-1 rounded-full bg-sky-500 shrink-0" aria-hidden="true" />
                            {c}
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-4 py-3">
                    <dt className="font-heading font-semibold text-slate-900 text-xs uppercase tracking-wider mb-1.5">Ideal For</dt>
                    <dd>
                      <ul className="space-y-1">
                        {specs.idealFor.map((u) => (
                          <li key={u} className="flex items-center gap-2 text-slate-700">
                            <span className="h-1 w-1 rounded-full bg-sky-500 shrink-0" aria-hidden="true" />
                            {u}
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={handleCTA}
          className={`mt-auto inline-flex items-center justify-center gap-2 py-2.5 rounded-lg font-heading font-semibold text-sm transition-all active:scale-[0.98] ${popular ? "bg-sky-600 hover:bg-sky-700 text-white" : "border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white"}`}
          data-testid={`dumpster-card-${size}-cta`}
        >
          {frontload ? "Request Front-Load Service" : "Request This Dumpster"} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
